<template>
  <div class="max-w-4xl mx-auto px-6 py-16">
    <div class="text-left">
      <div class="flex items-center gap-2 mb-4">
        <span v-html="EYE_ICON" class="text-gray-600"></span>
        <h1 class="text-2xl font-bold text-gray-900">My Watchlist</h1>
      </div>
      <p class="text-sm text-gray-500 mb-8">
        Your saved searches that you've added to get updates.
      </p>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-16">
        <div
          class="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-purple mx-auto"
        ></div>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="watchlistItems.length === 0"
        class="text-center py-16 bg-white rounded-lg border border-gray-200"
      >
        <div
          v-html="EYE_ICON"
          class="mx-auto mb-4 text-gray-300 w-16 h-16"
        ></div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">
          No watchlist items
        </h3>
        <p class="text-gray-500 mb-4">
          Add searches from the Search page to get updates when new transcripts
          match.
        </p>
        <router-link
          :to="{ name: 'InsightsSearch' }"
          class="inline-flex items-center gap-1 text-sm font-medium text-primary-purple hover:underline"
        >
          Go to Search
          <span v-html="ARROW_RIGHT_ICON"></span>
        </router-link>
      </div>

      <!-- Watchlist Cards -->
      <div v-else class="space-y-8">
        <!-- Recently Updated -->
        <div v-if="recentlyUpdatedItems.length > 0">
          <h2 class="text-lg font-semibold text-gray-900 mb-4">
            Recently Updated
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <WatchlistCard
              v-for="item in recentlyUpdatedItems"
              :key="item.id"
              :item="item"
              :open-menu-id="openMenuId"
              @click="handleCardClick(item)"
              @toggle-menu="openMenuId = openMenuId === $event ? null : $event"
              @view-details="
                viewDetailsItem = $event;
                openMenuId = null;
              "
              @remove="
                handleRemove($event);
                openMenuId = null;
              "
            />
          </div>
        </div>

        <!-- Other -->
        <div v-if="otherItems.length > 0">
          <h2 class="text-lg font-semibold text-gray-900 mb-4">Other</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <WatchlistCard
              v-for="item in otherItems"
              :key="item.id"
              :item="item"
              :open-menu-id="openMenuId"
              @click="handleCardClick(item)"
              @toggle-menu="openMenuId = openMenuId === $event ? null : $event"
              @view-details="
                viewDetailsItem = $event;
                openMenuId = null;
              "
              @remove="
                handleRemove($event);
                openMenuId = null;
              "
            />
          </div>
        </div>
      </div>
    </div>

    <WatchlistDetailsModal
      :show="!!viewDetailsItem"
      :item="viewDetailsItem"
      :program-name="userStore.selectedProgram?.name ?? ''"
      @close="viewDetailsItem = null"
      @updated="viewDetailsItem = null"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useQuery, useMutation } from "@vue/apollo-composable";
import gql from "graphql-tag";
import WatchlistCard from "@/components/insights/WatchlistCard.vue";
import WatchlistDetailsModal from "@/components/insights/WatchlistDetailsModal.vue";
import type { WatchlistItem } from "@/components/insights/WatchlistCard.vue";
import { useUserStore } from "@/stores/user";

const EYE_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>`;
const ARROW_RIGHT_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>`;

const LIST_WATCHLIST_QUERY = gql`
  query ListTranscriptWatchlist($limit: Int) {
    listTranscriptWatchlist(limit: $limit) {
      id
      query
      programId
      frequency
      addedAt
      initialResultCount
      previousResultCount
      currentResultCount
      resultsChanged
      lastRunAt
    }
  }
`;

const REMOVE_WATCHLIST_MUTATION = gql`
  mutation RemoveTranscriptWatchlist($input: RemoveTranscriptWatchlistInput!) {
    removeTranscriptWatchlist(input: $input) {
      success
      errors
    }
  }
`;

export default defineComponent({
  name: "InsightsWatchlistView",
  components: { WatchlistCard, WatchlistDetailsModal },
  setup() {
    const router = useRouter();
    const userStore = useUserStore();
    const openMenuId = ref<string | null>(null);
    const viewDetailsItem = ref<WatchlistItem | null>(null);

    const { result: watchlistResult, loading } = useQuery(
      LIST_WATCHLIST_QUERY,
      {},
      {
        fetchPolicy: "network-only",
      }
    );
    const { mutate: removeFromWatchlist } = useMutation(
      REMOVE_WATCHLIST_MUTATION,
      {
        refetchQueries: [{ query: LIST_WATCHLIST_QUERY }],
      }
    );

    const watchlistItems = computed(() => {
      const data = watchlistResult.value?.listTranscriptWatchlist;
      return (data || []) as WatchlistItem[];
    });

    const recentlyUpdatedItems = computed(() =>
      watchlistItems.value.filter((item) => item.resultsChanged)
    );
    const otherItems = computed(() =>
      watchlistItems.value.filter((item) => !item.resultsChanged)
    );

    const handleCardClick = (item: WatchlistItem) => {
      router.push({ name: "InsightsSearch", query: { q: item.query } });
    };

    const handleRemove = async (id: string) => {
      await removeFromWatchlist({ input: { id } });
    };

    return {
      EYE_ICON,
      ARROW_RIGHT_ICON,
      watchlistItems,
      recentlyUpdatedItems,
      otherItems,
      loading,
      openMenuId,
      viewDetailsItem,
      userStore,
      handleCardClick,
      handleRemove,
    };
  },
});
</script>
